import clsx from 'clsx'
import { format } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Avatar from '@/components-latest/primitives/Avatar'
import Counter from '@/components-latest/primitives/Counter'
import { ArrowDown } from '@/components-latest/primitives/Icons'
import Separator from '@/components-latest/primitives/Separator'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import Widget from '@/components-latest/primitives/Widget'
import OrderActions from '@/components-latest/ui/OrdersPage/OrderActions'
import OrderStatus from '@/components-latest/ui/OrdersPage/OrderStatus'
import PaymentDetalization from '@/components-latest/ui/OrdersPage/PaymentDetalization'
import PaymentSummary, {
  formatPrice
} from '@/components-latest/ui/OrdersPage/PaymentSummary'
import TimerTag from '@/components-latest/ui/OrdersPage/TImerTag/TimerTag'
import { isErrorType } from '@/config/http/http.helpers'
import history from '@/config/router/history'
import { SCROLL_ORDER_QUERY_ID } from '@/constants/common'
import {
  useLazyFetchOrderBillingsQuery,
  useLazyFetchOrderDetailsQuery
} from '@/ducks/orders/services/orders.api'
import { OrderItem, saveDriverName } from '@/ducks/orders/slices/orders.slice'
import {
  selectCurrencyCode,
  selectStoreId,
  selectStoreType
} from '@/ducks/store/selectors/store.selectors'
import useDynamicHeight from '@/hooks/useDynamicHeight/useDynamicHeight'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'
import { useToggle } from '@/hooks/useToggle/useToggle'
import { ORDERS_HEADER_ID } from '@/pages-latest/OrdersPage'

import Styles from './OrderDetails.module.scss'
import CourierDetails from '../CourierDetails'
import { useDispatch } from 'react-redux'
import { selectDriverNameByOrderId } from '@/ducks/orders/selectors/orders.selectors'
import { useFlags } from 'launchdarkly-react-client-sdk'

type IActions = 'accept' | 'reject' | 'return-to-stock' | 'ready'

interface IOrderDetailsProp {
  timeLeft?: number
  totalTime?: number
  onTimerEnd?: () => void
  order: OrderItem
  actions?: IActions[]
  onRefresh?: () => void
  shouldScrollTo?: boolean
  showBranch?: boolean
}

const OrderDetails: React.FC<IOrderDetailsProp> = ({
  timeLeft,
  totalTime,
  onTimerEnd,
  order,
  actions,
  onRefresh,
  shouldScrollTo,
  showBranch = false
}) => {
  const { t } = useTranslation()
  const { getTranslation } = useServerTranslation()
  const dispatch = useDispatch()
  const isDarkStore = useSelector(selectStoreType) === 'dark_store'
  const currency = useSelector(selectCurrencyCode) || ''
  const storeId = useSelector(selectStoreId) || ''
  const storeType = useSelector(selectStoreType)

  const [getDetails, detailsQuery] = useLazyFetchOrderDetailsQuery()
  const [getBillings, billingsQuery] = useLazyFetchOrderBillingsQuery()
  const driverName = useSelector(selectDriverNameByOrderId(order.id))
 // const driverName = detailsQuery?.data?.driver_name || t('No driver assigned')
  const remainingETA = detailsQuery?.data?.remainingETA
  const showCourierDetails = order.isAssigned

  const [open, toggleOpen, setOpen] = useToggle(false)
  const [preparationCounter, setPreparationCounter] = useState<number>(20)

  const hiddenRef = useDynamicHeight<HTMLDivElement>()

  const canBeReturnedToStock =
    storeType === 'dark_store' &&
    detailsQuery.isSuccess &&
    !detailsQuery.data?.isReturnedToStock

  const actionsMap = useMemo(
    () => ({
      accept: (
        <OrderActions.Accept
          orderId={order.id}
          preparationMinutes={preparationCounter}
          onRefresh={onRefresh}
        />
      ),
      reject: <OrderActions.Reject orderId={order.id} onRefresh={onRefresh} />,
      'return-to-stock': canBeReturnedToStock ? (
        <OrderActions.ReturnToStock
          onRefresh={() => {
            getDetails({ orderId: order.id, storeId })
          }}
          orderId={order.id}
        />
      ) : null,
      ready: <OrderActions.Ready orderId={order.id} onRefresh={onRefresh} />
    }),
    [
      onRefresh,
      order.id,
      preparationCounter,
      canBeReturnedToStock,
      storeId,
      getDetails
    ]
  )

  const toggleExpandableCreator = (forcedValue?: boolean) => () => {
    if (!detailsQuery.data) getDetails({ orderId: order.id, storeId })
    if (!billingsQuery.data) getBillings({ orderId: order.id, storeId })

    if (forcedValue) {
      setOpen(forcedValue)
    } else {
      toggleOpen()
    }
  }

  const ref = useRef<HTMLDivElement | null>(null)

  const location = useLocation()
  const query = new URLSearchParams(location.search)

  // todo if logic reused in another component make a hook
  useEffect(() => {
    if (!shouldScrollTo) return
    if (!ref.current) return

    toggleExpandableCreator(true)()

    const headerHeight =
      document.getElementById(ORDERS_HEADER_ID)?.offsetHeight || 0
    const orderDetailsPos =
      ref.current.getBoundingClientRect().top + window.scrollY
    // 8 is to have a little margin from top of the screen (so the block would not be sticky to top)
    const offsetPosition = orderDetailsPos - headerHeight - 8

    let isScrolled = false
    if (billingsQuery?.data && detailsQuery?.data) {
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      isScrolled = true
    }

    if (isScrolled) {
      query.delete(SCROLL_ORDER_QUERY_ID)
      history.replace({ search: query.toString() })
    }
  }, [shouldScrollTo, billingsQuery.data, detailsQuery.data])

  useEffect(() => {
    if (detailsQuery?.data?.driver_name) {
      dispatch(saveDriverName({ orderId: order.id, driverName: detailsQuery.data.driver_name }));
    }
  }, [dispatch, order.id, detailsQuery?.data?.driver_name]);
  
  return (
    <div
      ref={ref}
      className={clsx(Styles.orderDetails, { [Styles.open]: open })}
      data-testid={`order-details-${order.id}`}>
      {/*<button onClick={onClick}>{order.id}</button>*/}
      <header className={Styles.header}>
        <div className={Styles.tags}>
            {order.isAssigned ? (
                <OrderStatus assigned={order.isAssigned} status={order.status} />
              ) : (
                [timeLeft, totalTime].every(time => typeof time === 'number') ? (
                  <TimerTag
                    timeLeft={timeLeft as number}
                    totalTime={totalTime as number}
                    onTimerEnd={() => onTimerEnd?.()}
                  />
                ) : (
                  <OrderStatus assigned={order.isAssigned} status={order.status} />
                )
              )}
          <Text variant="nextCaption" weight="semiBold" className={Styles.tag}>
            {t('orders.tags.classic')}
          </Text>
        </div>
        <div className={Styles.metas}>
          <Text className={Styles.date} variant="nextCaption">
            {format(order.orderTime, "MMM dd, yyyy '·' HH:mm")}
          </Text>
          <Text variant="nextBody" weight="bold">
            {t('orders.count-items', { count: order.foodCount })}
          </Text>
        </div>
        <div className={Styles.additional}>
          <div className={Styles.total}>
            <Text className={Styles.totalLabel} variant="nextBody">
              {t('orders.total')}
            </Text>
            <Text variant="nextTitle-3">
              {formatPrice(order.restaurantPayout || 0, currency, {
                signDisplay: 'never'
              })}
            </Text>
          </div>
          <Widget
            onClick={toggleExpandableCreator()}
            className={Styles.expandButton}>
            {open ? t('orders.collapse') : t('orders.expand')}
            <ArrowDown
              className={clsx(Styles.arrow, open && Styles.rotate180)}
            />
          </Widget>
        </div>
      </header>
      {/* TODO: Rework details error handling when request updated to singular for every id */}
      <div className={Styles.partiallyHidden} ref={hiddenRef}>
        <div className={Styles.hidden}>
          <Separator variant="squared" />
          <div className={Styles.details}>
            <div>
              {detailsQuery.data ? (
                <PaymentDetalization
                  variant={isDarkStore ? 'darkStore' : 'default'}
                  products={detailsQuery.data.foods}
                  className={clsx(Styles.detalization, Styles.block)}
                />
              ) : detailsQuery.error ? (
                <div className={clsx(Styles.centeredWrapper, Styles.block)}>
                  <Text variant="nextCaption" align="center">
                    {t('orders.error.loading-details')}
                  </Text>
                </div>
              ) : (
                detailsQuery.isFetching && (
                  <div className={clsx(Styles.centeredWrapper, Styles.block)}>
                    <Spinner />
                  </div>
                )
              )}
            </div>

            <div className={Styles.additionalDetails}>
              {/* Conditionally render CourierDetails */}
              {showCourierDetails &&  (
                <CourierDetails
                  driverName={driverName || "No driver assigned"}
                  remainingETA={remainingETA ?? 0}
                  totalTime={totalTime || 0}
                  onTimerEnd={onTimerEnd}
                  avatarUrl={''}
                  status={order.status}
                  orderTime={0}
                />
              )}
              {/* Cooking Time Section */}
              {actions?.includes('accept') && (
                <div className={clsx(Styles.block, Styles.cookingTime)}>
                  <Text variant="nextSubtitle" weight="bold">
                    {t('orders.cooking-time')}
                  </Text>
                  <Counter
                    onChange={value => setPreparationCounter(value)}
                    value={preparationCounter}
                    step={5}
                    minNumber={10}
                    maxNumber={60}
                  />
                </div>
              )}

              {/* Billing Summary Section */}
              {billingsQuery.data ? (
                <PaymentSummary
                  billing={billingsQuery.data.billings}
                  status={order.status}
                  isEpay={billingsQuery.data.isEpay}
                  className={clsx(Styles.summary, Styles.block)}
                />
              ) : billingsQuery.error ? (
                <div className={clsx(Styles.centeredWrapper, Styles.block)}>
                  <Text variant="nextCaption" align="center">
                    {isErrorType(billingsQuery.error) &&
                      billingsQuery.error.message}
                  </Text>
                </div>
              ) : (
                billingsQuery.isFetching && (
                  <div className={clsx(Styles.centeredWrapper, Styles.block)}>
                    <Spinner />
                  </div>
                )
              )}

              {/* Order Metadata */}
              <Text className={Styles.orderId}>{order.id}</Text>
              <Text
                className={Styles.date}
                variant="nextCaption"
                align="center">
                {format(order.orderTime, "MMM dd, yyyy '·' HH:mm")}
              </Text>
            </div>
          </div>

          <Separator variant="squared" />
        </div>
      </div>

      <footer className={Styles.footer}>
        {showBranch && order.child_details && (
          <div className={clsx(Styles.user, Styles.branch)}>
            <Avatar
              size="small"
              string={getTranslation(order.child_details.name)}
              image={order.child_details.avatar}
            />
            <div>
              <Text
                className={Styles.userType}
                variant="nextOverline"
                weight="bold"
                gutter={4}>
                {t('orders.user-types.branch')}
              </Text>
              <Text variant="nextBody" weight="bold">
                {getTranslation(order.child_details.name)}
              </Text>
            </div>
          </div>
        )}
        <div className={Styles.user}>
          <Avatar
            size="small"
            string={`${order.user.firstName} ${order.user.lastName}`}
            image={order.user.avatarUrl}
          />
          <div>
            <Text
              className={Styles.userType}
              variant="nextOverline"
              weight="bold"
              gutter={4}>
              {t('orders.user-types.client')}
            </Text>
            <Text variant="nextBody" weight="bold">
              {`${order.user.firstName} ${order.user.lastName}`}
            </Text>
          </div>
        </div>
        <div className={Styles.actions}>
          {actions?.map(action => (
            <React.Fragment key={action}>{actionsMap[action]}</React.Fragment>
          ))}
        </div>
      </footer>
    </div>
  )
}

export default OrderDetails
